import { iframeDataBridge } from '@/iframe/iframeDataBridge.js';
import { authenticatedFetch, getApiUrl } from '@/utils/auth/authService.js';
import { log } from '@/utils/core/vueLogger.js';
import { CacheManager } from '@/utils/core/cacheManager.js';

const attributeCache = new CacheManager('VueCache', 'cache', {
	maxAge: 24 * 60 * 60 * 1000,
	logCategory: 'Attribute',
	keyPrefix: () => `${iframeDataBridge.getCustomer() ?? 'unknown'}.${iframeDataBridge.getCurrentUser()?.id ?? 'unknown'}.attributes.`,
});

export async function getAttributes(forceRefresh = false, tokenType = 'token') {
	try {
		if (!forceRefresh) {
			const cached = await attributeCache.get('all');
			if (cached) {
				log('info', 'Attribute', 'Returning cached attributes');
				return cached;
			}

			try {
				const legacyData = await iframeDataBridge.getStorageAsync('ATTRIBUTES');
				if (legacyData) {
					log('info', 'Attribute', '📦 Seeding Vue cache from legacy storage (ATTRIBUTES)');
					await attributeCache.put('all', legacyData);
					return legacyData;
				}
			} catch (legacyError) {
				log('warn', 'Attribute', `⚠️ Legacy storage check failed: ${legacyError.message}`);
			}
		}

		if (iframeDataBridge.isOffline() && !forceRefresh) {
			log('warn', 'Attribute', 'Offline: No cached attributes available');
			return [];
		}

		const apiUrl = getApiUrl('Core/Attributes');
		const response = await authenticatedFetch(apiUrl, {}, tokenType);

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		const data = await response.json();

		await attributeCache.put('all', data);

		log('info', 'Attribute', `Fetched ${data?.length || 0} attributes`);
		return data;
	} catch (error) {
		log('error', 'Attribute', `Error loading attributes: ${error.message}`);

		const cached = await attributeCache.get('all');
		if (cached) {
			log('info', 'Attribute', 'Returning cached attributes after error');
			return cached;
		}

		throw error;
	}
}

export async function getAttributeById(id, tokenType = 'token') {
	try {
		const cached = await attributeCache.get(id);
		if (cached) {
			return cached;
		}

		const allCached = await attributeCache.get('all');
		if (allCached) {
			const found = allCached.find((a) => Number(a.id) === Number(id));
			if (found) return found;
		}

		if (iframeDataBridge.isOffline()) {
			log('warn', 'Attribute', `Offline: Attribute ${id} not found in cache`);
			return null;
		}

		const apiUrl = getApiUrl(`Core/Attributes/${id}`);
		const response = await authenticatedFetch(apiUrl, {}, tokenType);

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		const data = await response.json();
		await attributeCache.put(id, data);

		return data;
	} catch (error) {
		log('error', 'Attribute', `Error loading attribute ${id}: ${error.message}`);
		throw error;
	}
}

export async function clearAttributeCache(id = null) {
	if (id) {
		await attributeCache.delete(id);
	} else {
		await attributeCache.clearAll();
	}
}

export async function cleanOldCacheEntries(maxAgeInDays = 1) {
	await attributeCache.cleanOld(maxAgeInDays);
}

export async function getCacheStats() {
	return await attributeCache.getStats();
}

cleanOldCacheEntries();
